import React from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, ShoppingBag, User, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

// 3.30 — UseCasesSection: storytelling 3 personas (Agency / PMI / Solo)
// Highest emotional pull: "io sono come Marco/Sara/Luca"
const UseCasesSection = () => {
  const { t } = useTranslation();

  const personas = [
    {
      id: 'agency',
      icon: Briefcase,
      iconBg: 'from-[#0055FF] to-[#06B6D4]',
      ringColor: '#0055FF',
    },
    {
      id: 'pmi',
      icon: ShoppingBag,
      iconBg: 'from-[#F59E0B] to-[#EC4899]',
      ringColor: '#EC4899',
    },
    {
      id: 'solo',
      icon: User,
      iconBg: 'from-[#10B981] to-[#00E5FF]',
      ringColor: '#10B981',
    },
  ];

  return (
    <section className="py-24 px-6 bg-[#050505] border-t border-white/5 relative overflow-hidden" data-testid="usecases-section">
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-[#0055FF]/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#10B981]/6 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 animate-fade-up">
          <span className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-semibold">
            {t('useCases.eyebrow')}
          </span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mt-4 mb-6">
            {t('useCases.title1')}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0055FF] to-[#10B981]">
              {t('useCases.title2')}
            </span>
          </h2>
          <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto">
            {t('useCases.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {personas.map((p, idx) => {
            const Icon = p.icon;
            const outcomes = t(`useCases.personas.${p.id}.outcomes`, { returnObjects: true });
            const outcomeList = Array.isArray(outcomes) ? outcomes : [];
            return (
              <div
                key={p.id}
                className="relative rounded-2xl p-7 border border-white/10 bg-white/[0.02] hover:border-white/20 hover:-translate-y-1 transition-all animate-fade-up"
                style={{ animationDelay: `${idx * 0.1}s` }}
                data-testid={`usecase-card-${p.id}`}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${p.iconBg} flex items-center justify-center mb-5`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>

                <div className="mb-2">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] font-semibold">
                    {t(`useCases.personas.${p.id}.role`)}
                  </span>
                </div>

                <h3 className="text-2xl font-['Outfit'] font-medium mb-1 text-white">
                  {t(`useCases.personas.${p.id}.name`)}
                </h3>

                <p className="text-sm text-[#A1A1AA] mb-5 italic leading-relaxed">
                  &ldquo;{t(`useCases.personas.${p.id}.quote`)}&rdquo;
                </p>

                <div className="rounded-xl bg-black/40 border border-white/5 p-4 mb-5">
                  <p className="text-sm text-[#d4d4d8] leading-relaxed">
                    {t(`useCases.personas.${p.id}.story`)}
                  </p>
                </div>

                <div className="space-y-2.5">
                  <div className="text-[11px] uppercase tracking-wider font-semibold mb-2" style={{ color: p.ringColor }}>
                    {t('useCases.outcomesLabel')}
                  </div>
                  {outcomeList.map((o, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm text-[#d4d4d8]">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: p.ringColor }} />
                      <span>{o}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-white/5">
                  <div className="text-xs text-[#71717A]">
                    {t('useCases.resultLabel')}
                  </div>
                  <div className="text-base font-semibold text-white mt-1">
                    {t(`useCases.personas.${p.id}.result`)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 text-center animate-fade-up">
          <p className="text-base text-[#A1A1AA] mb-6">
            {t('useCases.ctaText')}
          </p>
          <Link to="/register">
            <button className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-[#0055FF] hover:bg-[#3377FF] text-white font-medium transition-colors">
              {t('useCases.ctaButton')}
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
